import { useRef, useState, useCallback, useEffect } from "react";
import styled from "styled-components";
import lottie from "lottie-web";

const LottieIcon = ({
  animationData,
  trigger = "click",
  size = 48,
  loop = false,
  speed = 1, // 1 = default speed, 1.5 = 1.5x, 2 = 2x
  autoplay = false,
  hoverScale = 1.1,
  absolute = false,
  strokeColor,
  strokeWidth,
  marginBottom,

  // Toggle props (used with "toggle" trigger and segments)
  isToggled = false,
  onToggle,
  likeSegment = [1, 90],
  unlikeSegment = [90, 180],

  // Toggle-reverse props (used with "toggle-reverse")
  isActive = false,
  onActiveChange,

  // Hover props
  reverseSpeed,

  // Callbacks
  onComplete,
  onClick,
  onMouseEnter,
  onMouseLeave,
  className,
  style,
  lottieRef,
  ...rest
}) => {
  const containerRef = useRef(null);
  const animationRef = useRef(null);

  const [isPlaying, setIsPlaying] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);

  // Initialize Lottie animation
  useEffect(() => {
    if (!containerRef.current || !animationData) return;

    if (animationRef.current) {
      animationRef.current.destroy();
    }

    const shouldLoop = trigger === "loop" ? true : loop;
    const shouldAutoplay = trigger === "loop" ? true : autoplay;

    animationRef.current = lottie.loadAnimation({
      container: containerRef.current,
      renderer: "svg",
      loop: typeof shouldLoop === "number" ? shouldLoop : shouldLoop,
      autoplay: shouldAutoplay,
      animationData,
    });

    // Set initial animation speed
    animationRef.current.setSpeed(speed);

    // For "toggle" trigger, jump to the correct initial frame
    if (trigger === "toggle") {
      const initialFrame = isToggled ? likeSegment[1] : unlikeSegment[1];
      animationRef.current.goToAndStop(initialFrame, true);
    }

    // For "toggle-reverse", set initial frame based on active state
    if (trigger === "toggle-reverse") {
      if (isActive) {
        animationRef.current.goToAndStop(
          animationRef.current.totalFrames - 1,
          true,
        );
      } else {
        animationRef.current.goToAndStop(0, true);
      }
    }

    // Handle animation completion
    animationRef.current.addEventListener("complete", () => {
      setIsPlaying(false);
      onComplete?.();
    });

    // Expose animation instance via ref if provided
    if (lottieRef) {
      lottieRef.current = animationRef.current;
    }

    setIsInitialized(true);

    // Cleanup on unmount
    return () => {
      if (animationRef.current) {
        animationRef.current.destroy();
      }
      setIsInitialized(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [animationData]);

  // React to speed changes dynamically
  useEffect(() => {
    if (!animationRef.current) return;
    animationRef.current.setSpeed(speed);
  }, [speed]);

  // Dynamically update stroke color inside the SVG
  useEffect(() => {
    if (!animationRef.current || !containerRef.current || !strokeColor) return;

    const svg = containerRef.current.querySelector("svg");
    if (svg) {
      const strokes = svg.querySelectorAll("[stroke]");
      strokes.forEach((el) => {
        el.setAttribute("stroke", strokeColor);
      });
    }
  }, [strokeColor, isInitialized]);

  // Dynamically update stroke width inside the SVG
  useEffect(() => {
    if (!animationRef.current || !containerRef.current || !strokeWidth) return;

    const svg = containerRef.current.querySelector("svg");
    if (svg) {
      const strokes = svg.querySelectorAll("[stroke]");
      strokes.forEach((el) => {
        el.setAttribute("stroke-width", strokeWidth);
      });
    }
  }, [strokeWidth, isInitialized]);

  // React to toggle state changes (segment-based toggle)
  useEffect(() => {
    if (trigger !== "toggle" || !animationRef.current || !isInitialized) return;

    const segment = isToggled ? likeSegment : unlikeSegment;
    animationRef.current.setSpeed(speed);
    animationRef.current.playSegments(segment, true);
    setIsPlaying(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isToggled]);

  // React to isActive changes (toggle-reverse behavior)
  useEffect(() => {
    if (trigger !== "toggle-reverse" || !animationRef.current || !isInitialized)
      return;

    animationRef.current.setSpeed(speed);

    if (isActive) {
      // Play forward - if already playing (mid-animation), just reverse direction from current frame
      animationRef.current.setDirection(1);
      if (!isPlaying) {
        animationRef.current.goToAndStop(0, true);
      }
      animationRef.current.play();
    } else {
      // Play backward - if already playing (mid-animation), just reverse direction from current frame
      animationRef.current.setDirection(-1);
      if (!isPlaying) {
        animationRef.current.goToAndStop(
          animationRef.current.totalFrames - 1,
          true,
        );
      }
      animationRef.current.play();
    }

    setIsPlaying(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive]);

  // Play animation on click
  const playClick = useCallback(() => {
    if (!animationRef.current) return;

    animationRef.current.goToAndStop(0, true);
    animationRef.current.setDirection(1);
    animationRef.current.setSpeed(speed);
    animationRef.current.play();
    setIsPlaying(true);
  }, [speed]);

  // Play animation on hover enter
  const playHoverIn = useCallback(() => {
    if (!animationRef.current) return;

    animationRef.current.setDirection(1);
    animationRef.current.setSpeed(speed);
    animationRef.current.play();
    setIsPlaying(true);
  }, [speed]);

  // Reverse animation on hover leave
  const playHoverOut = useCallback(() => {
    if (!animationRef.current) return;

    animationRef.current.setDirection(-1);
    animationRef.current.setSpeed(reverseSpeed ?? speed * 2);
    animationRef.current.play();
    setIsPlaying(true);
  }, [speed, reverseSpeed]);

  // Click handler
  const handleClick = useCallback(
    (e) => {
      if (trigger === "click") {
        if (isPlaying && !loop) return;
        playClick();
      } else if (trigger === "toggle") {
        onToggle?.(!isToggled);
      } else if (trigger === "toggle-reverse") {
        onActiveChange?.(!isActive);
      }

      onClick?.(e);
    },
    [
      trigger,
      isPlaying,
      loop,
      playClick,
      isToggled,
      onToggle,
      isActive,
      onActiveChange,
      onClick,
    ],
  );

  // Mouse enter handler
  const handleMouseEnter = useCallback(
    (e) => {
      if (trigger === "hover") {
        playHoverIn();
      }
      onMouseEnter?.(e);
    },
    [trigger, playHoverIn, onMouseEnter],
  );

  // Mouse leave handler
  const handleMouseLeave = useCallback(
    (e) => {
      if (trigger === "hover") {
        playHoverOut();
      }
      onMouseLeave?.(e);
    },
    [trigger, playHoverOut, onMouseLeave],
  );

  // Determine if the icon should behave as a clickable element
  const isClickable =
    trigger === "click" ||
    trigger === "toggle" ||
    trigger === "toggle-reverse" ||
    !!onClick;

  return (
    <IconWrapper
      $size={size}
      $absolute={absolute}
      $clickable={isClickable}
      $hoverScale={trigger === "hover" || isClickable ? hoverScale : 1}
      className={className}
      style={style}
      onClick={handleClick}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      role={isClickable ? "button" : undefined}
      tabIndex={isClickable ? 0 : undefined}
      aria-pressed={
        trigger === "toggle"
          ? isToggled
          : trigger === "toggle-reverse"
            ? isActive
            : undefined
      }
      onKeyDown={(e) => {
        if (isClickable && (e.key === "Enter" || e.key === " ")) {
          e.preventDefault();
          handleClick(e);
        }
      }}
      {...rest}
    >
      <AnimationContainer ref={containerRef} />
    </IconWrapper>
  );
};

// Hook for imperative control of the Lottie animation
export const useLottieIcon = () => {
  const lottieRef = useRef(null);

  const play = useCallback(() => {
    lottieRef.current?.play();
  }, []);

  const stop = useCallback(() => {
    lottieRef.current?.stop();
  }, []);

  const pause = useCallback(() => {
    lottieRef.current?.pause();
  }, []);

  const goToAndStop = useCallback((frame, isFrame = true) => {
    lottieRef.current?.goToAndStop(frame, isFrame);
  }, []);

  const goToAndPlay = useCallback((frame, isFrame = true) => {
    lottieRef.current?.goToAndPlay(frame, isFrame);
  }, []);

  const playSegments = useCallback((segments, forceFlag = true) => {
    lottieRef.current?.playSegments(segments, forceFlag);
  }, []);

  const setSpeed = useCallback((speed) => {
    lottieRef.current?.setSpeed(speed);
  }, []);

  const setDirection = useCallback((direction) => {
    lottieRef.current?.setDirection(direction);
  }, []);

  const getDuration = useCallback((inFrames = false) => {
    return lottieRef.current?.getDuration(inFrames);
  }, []);

  return {
    lottieRef,
    play,
    stop,
    pause,
    goToAndStop,
    goToAndPlay,
    playSegments,
    setSpeed,
    setDirection,
    getDuration,
  };
};

export default LottieIcon;

const IconWrapper = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  position: ${({ $absolute }) => ($absolute ? "absolute" : "relative")};

  width: ${({ $size }) => $size}px;
  height: ${({ $size }) => $size}px;
  margin-bottom: ${({ $marginBottom }) => $marginBottom}px;
  cursor: ${({ $clickable }) => ($clickable ? "pointer" : "default")};
  user-select: none;
  transition:
    transform 0.2s ease,
    opacity 0.2s ease;
`;

const AnimationContainer = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    width: 100% !important;
    height: 100% !important;
  }
`;
